import {
  Color,
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardNodeMaterial,
  RingGeometry,
  Vector3,
  type Object3D,
  type PerspectiveCamera,
  type Scene,
} from 'three/webgpu';
import { clamp } from '@stillsun/shared';
import type { Vec3 } from '@stillsun/shared';

/**
 * What a beacon needs to draw itself.
 *
 * Protocol v3 took `ExtractSnap` off the wire with the rest of the raid loop — the city and the
 * zone are persistent rooms and nobody extracts from them. The shape stays here, local, because
 * the pillars are still the cleanest marker the game has for a place you have to stand in, and
 * the first dungeon will want them back. Delete this file when it is certain nothing does.
 */
export interface ExtractSnap {
  id: number;
  pos: Vec3;
  active: boolean;
  /** Accumulated seconds of standing, not a fraction. */
  progress: number;
  /** Ring radius in metres. Baked into geometry, so only a rebuild can change it. */
  radius: number;
}
import { getProp } from './props.js';
import { requestLatePrecompile } from './renderer.js';

/**
 * Seconds of continuous standing inside the ring that a full extract takes. The server
 * (room.ts EXTRACT_SECONDS) sends `progress` as *accumulated seconds*, not a fraction,
 * so the ring needs this denominator locally. Keep the two in step.
 */
const EXTRACT_HOLD_SECONDS = 8;

const PILLAR_HEIGHT = 14;
const PILLAR_RADIUS = 0.5;
/**
 * Height of the generated beacon's lantern head above its feet, in metres. The tripod is
 * 3.5 m tall and the lantern body occupies roughly its top 0.75 m; starting the pillar
 * here makes the light column read as something the beacon emits rather than a shaft
 * driven through its legs. Only applied once the GLB is actually in the scene.
 */
const PILLAR_BASE_ON_MODEL = 3.0;
/** Label anchor: high on the pillar but not at the tip, so it stays clear of the beam. */
const LABEL_HEIGHT = 9;
/** DOM writes are the expensive part; 20 Hz is smooth enough for text that reads in metres. */
const LABEL_INTERVAL = 1 / 20;
/** r=15 in the 36x36 viewBox the CSS already styles. */
const RING_CIRCUMFERENCE = 2 * Math.PI * 15;

const AMBER = 0xffa040;
/** Unlit extracts are cold hull grey: present on the map, but plainly not open. */
const COLD = 0x6a6f74;

interface Beacon {
  id: number;
  pos: Vec3;
  radius: number;
  active: boolean;
  progress: number;
  group: Group;
  pillar: Mesh;
  /** The generated tripod, once loaded. Null means the bare pillar is all there is. */
  model: Object3D | null;
  pillarMat: MeshStandardNodeMaterial;
  ringMat: MeshStandardNodeMaterial;
  ringGeo: RingGeometry;
  label: HTMLDivElement;
  chev: HTMLDivElement;
  dist: HTMLDivElement;
  ringSvg: SVGSVGElement;
  ringFg: SVGCircleElement;
  /** Last values written to the DOM, so the throttled tick can skip no-op writes. */
  lastMetres: number;
  lastProgress: number;
}

/** One cylinder is enough for every extract; only the ring depends on the radius. */
let pillarGeo: CylinderGeometry | null = null;
function pillarGeometry(): CylinderGeometry {
  pillarGeo ??= new CylinderGeometry(PILLAR_RADIUS, PILLAR_RADIUS, PILLAR_HEIGHT, 16, 1);
  return pillarGeo;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

function makeLabel(id: number): {
  label: HTMLDivElement;
  chev: HTMLDivElement;
  dist: HTMLDivElement;
  ringSvg: SVGSVGElement;
  ringFg: SVGCircleElement;
} {
  const label = document.createElement('div');
  label.className = 'wm off';

  const chev = document.createElement('div');
  chev.className = 'chev';
  chev.textContent = `▲ EXIT ${String(id)}`;

  const dist = document.createElement('div');
  dist.className = 'dist';
  dist.textContent = '';

  // Built once and hidden, not created on the first tick of a hold — allocating DOM
  // at the moment the player starts extracting is exactly the wrong time for it.
  const ringSvg = document.createElementNS(SVG_NS, 'svg');
  ringSvg.setAttribute('class', 'ring');
  ringSvg.setAttribute('viewBox', '0 0 36 36');
  const bg = document.createElementNS(SVG_NS, 'circle');
  bg.setAttribute('class', 'bg');
  bg.setAttribute('cx', '18');
  bg.setAttribute('cy', '18');
  bg.setAttribute('r', '15');
  const fg = document.createElementNS(SVG_NS, 'circle');
  fg.setAttribute('class', 'fg');
  fg.setAttribute('cx', '18');
  fg.setAttribute('cy', '18');
  fg.setAttribute('r', '15');
  fg.setAttribute('stroke-dasharray', String(RING_CIRCUMFERENCE));
  fg.setAttribute('stroke-dashoffset', String(RING_CIRCUMFERENCE));
  ringSvg.append(bg, fg);
  ringSvg.style.display = 'none';

  // Order matters for the flex column: the ring sits below the text so it appearing
  // mid-hold does not shove the name and distance around.
  label.append(chev, dist, ringSvg);
  return { label, chev, dist, ringSvg, ringFg: fg };
}

export class ExtractBeacons {
  private readonly scene: Scene;
  private readonly labelRoot: HTMLElement;
  private readonly root = new Group();
  private readonly beacons: Beacon[] = [];
  private visible = true;
  private clock = 0;
  /** Primed above the interval so labels land in the right place on the very first frame. */
  private labelAcc = LABEL_INTERVAL;
  private readonly tmp = new Vector3();
  private readonly tmpView = new Vector3();

  constructor(scene: Scene, labelRoot: HTMLElement) {
    this.scene = scene;
    this.labelRoot = labelRoot;
    this.root.name = 'extracts';
    this.scene.add(this.root);
  }

  /**
   * RaidState arrives every ~half second. The id set almost never changes, so the
   * common path only writes `active`/`progress` into the existing beacons.
   */
  setExtracts(list: readonly ExtractSnap[]): void {
    let same = list.length === this.beacons.length;
    if (same) {
      for (let i = 0; i < list.length; i++) {
        if (list[i]?.id !== this.beacons[i]?.id) {
          same = false;
          break;
        }
      }
    }
    if (!same) {
      this.clear();
      for (const x of list) {
        const b = this.build(x);
        this.beacons.push(b);
        this.attachModel(b);
      }
    }
    for (let i = 0; i < list.length; i++) {
      const x = list[i];
      const b = this.beacons[i];
      if (!x || !b) continue;
      const wasActive = b.active;
      b.active = x.active;
      b.progress = x.progress;
      // The ring radius is baked into geometry; only a rebuild can change it, and the
      // server never moves or resizes an extract mid-raid.
      if (x.pos.x !== b.pos.x || x.pos.y !== b.pos.y || x.pos.z !== b.pos.z) {
        b.pos = { ...x.pos };
        b.group.position.set(x.pos.x, x.pos.y, x.pos.z);
      }
      if (wasActive !== b.active) this.paint(b);
    }
  }

  private build(x: ExtractSnap): Beacon {
    const group = new Group();
    group.name = `extract:${String(x.id)}`;
    group.position.set(x.pos.x, x.pos.y, x.pos.z);

    // Per-beacon materials: active and inactive extracts differ in colour, and the
    // active ones pulse independently of each other.
    const pillarMat = new MeshStandardNodeMaterial({
      color: 0x211508,
      emissive: new Color(AMBER),
      emissiveIntensity: 5,
      roughness: 0.55,
      metalness: 0,
    });
    const pillar = new Mesh(pillarGeometry(), pillarMat);
    pillar.position.y = PILLAR_HEIGHT / 2; // cylinder origin is its centre
    pillar.castShadow = false;
    pillar.receiveShadow = false;
    group.add(pillar);

    const radius = Math.max(0.5, x.radius);
    const ringGeo = new RingGeometry(Math.max(0.1, radius - 0.18), radius, 48, 1);
    const ringMat = new MeshStandardNodeMaterial({
      color: 0x211508,
      emissive: new Color(AMBER),
      emissiveIntensity: 5,
      roughness: 0.6,
      metalness: 0,
    });
    const ring = new Mesh(ringGeo, ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.03; // clear of the ground plane, which is coplanar at y = 0
    group.add(ring);

    this.root.add(group);
    // The pillar GLB and its two materials arrive with the first extract snapshot, after the
    // deploy precompile; without this they compiled inside the first frame that drew them.
    requestLatePrecompile('extracts');

    const dom = makeLabel(x.id);
    this.labelRoot.appendChild(dom.label);

    const b: Beacon = {
      id: x.id,
      pos: { ...x.pos },
      radius,
      active: x.active,
      progress: x.progress,
      group,
      pillarMat,
      ringMat,
      ringGeo,
      label: dom.label,
      chev: dom.chev,
      dist: dom.dist,
      ringSvg: dom.ringSvg,
      ringFg: dom.ringFg,
      lastMetres: -1,
      lastProgress: -1,
      pillar,
      model: null,
    };
    this.paint(b);
    return b;
  }

  /**
   * Puts the generated tripod under the light column once it arrives. The pillar and the
   * ring are the gameplay read and are built first, so a missing GLB costs an extract
   * nothing. The membership check covers a `clear()` landing while the load is in flight.
   */
  private attachModel(b: Beacon): void {
    void getProp('extract-beacon').then((model) => {
      if (!model || !this.beacons.includes(b)) return;
      b.group.add(model);
      b.model = model;
      b.pillar.position.y = PILLAR_BASE_ON_MODEL + PILLAR_HEIGHT / 2;
    });
  }

  /** Applies the active/inactive look. Called on build and only when `active` flips. */
  private paint(b: Beacon): void {
    const c = b.active ? AMBER : COLD;
    const e = b.active ? 5 : 0.4;
    b.pillarMat.emissive.setHex(c);
    b.pillarMat.emissiveIntensity = e;
    b.ringMat.emissive.setHex(c);
    b.ringMat.emissiveIntensity = e;
    b.label.classList.toggle('inactive', !b.active);
  }

  update(dt: number, camera: PerspectiveCamera, viewer: Vec3): void {
    if (!this.visible) return;
    this.clock += dt;

    // The 3D pulse runs every frame — at 120 Hz a throttled glow reads as a stutter.
    const pulse = 5 + 2 * Math.sin(this.clock * Math.PI); // ~0.5 Hz, 3 -> 7
    for (const b of this.beacons) {
      if (!b.active) continue;
      b.pillarMat.emissiveIntensity = pulse;
      b.ringMat.emissiveIntensity = pulse;
    }

    this.labelAcc += dt;
    if (this.labelAcc < LABEL_INTERVAL) return;
    this.labelAcc = 0;

    // Read the layout box once: each access forces a style recalc.
    const w = this.labelRoot.clientWidth;
    const h = this.labelRoot.clientHeight;
    if (w === 0 || h === 0) return;

    for (const b of this.beacons) {
      this.placeLabel(b, camera, viewer, w, h);
    }
  }

  private placeLabel(
    b: Beacon,
    camera: PerspectiveCamera,
    viewer: Vec3,
    w: number,
    h: number,
  ): void {
    this.tmp.set(b.pos.x, b.pos.y + LABEL_HEIGHT, b.pos.z);
    // View space, not `ndc.z > 1`: a point behind the eye divides by a negative w, so
    // its NDC x/y come back mirrored and cannot be edge-clamped meaningfully anyway.
    this.tmpView.copy(this.tmp).applyMatrix4(camera.matrixWorldInverse);
    const behind = this.tmpView.z >= -camera.near;

    let off = behind;
    if (!off) {
      this.tmp.project(camera);
      // A little slack past the edge so a label does not blink at the screen border.
      off = Math.abs(this.tmp.x) > 1.15 || Math.abs(this.tmp.y) > 1.15;
    }
    if (off) {
      // Chosen over edge-clamping with the chevron as an off-screen arrow: a 14 m
      // emissive pillar is already visible from anywhere in the yard, so a permanent
      // ring of arrows round the viewport would add clutter without adding information.
      b.label.classList.add('off');
      return;
    }
    b.label.classList.remove('off');
    b.label.style.left = `${String(Math.round((this.tmp.x * 0.5 + 0.5) * w))}px`;
    b.label.style.top = `${String(Math.round((-this.tmp.y * 0.5 + 0.5) * h))}px`;

    const metres = Math.round(
      Math.hypot(b.pos.x - viewer.x, b.pos.y - viewer.y, b.pos.z - viewer.z),
    );
    if (metres !== b.lastMetres) {
      b.lastMetres = metres;
      b.dist.textContent = `${String(metres)} m`;
    }

    const frac = clamp(b.progress / EXTRACT_HOLD_SECONDS, 0, 1);
    if (frac !== b.lastProgress) {
      b.lastProgress = frac;
      // The ring is the hold meter; showing an empty one on every beacon would read as
      // "extraction available here" when nothing is happening.
      b.ringSvg.style.display = frac > 0 ? '' : 'none';
      if (frac > 0) {
        b.ringFg.setAttribute('stroke-dashoffset', String(RING_CIRCUMFERENCE * (1 - frac)));
      }
    }
  }

  setVisible(on: boolean): void {
    this.visible = on;
    this.root.visible = on;
    // One-directional on purpose: hiding is immediate, but un-hiding is left to the
    // next label tick, which is the first moment left/top are known to be current.
    // Clearing `.off` here would flash a stale (or never-written) position for a frame.
    if (on) this.labelAcc = LABEL_INTERVAL;
    else for (const b of this.beacons) b.label.classList.add('off');
  }

  /** Tears down the beacons but leaves the instance ready for another `setExtracts`. */
  clear(): void {
    for (const b of this.beacons) {
      this.root.remove(b.group);
      b.pillarMat.dispose();
      b.ringMat.dispose();
      b.ringGeo.dispose();
      b.label.remove();
    }
    this.beacons.length = 0;
  }

  /** `clear()` plus detaching from the scene. The shared pillar geometry is left alone
   * so a later instance can reuse it, matching how the rest of the renderer behaves. */
  dispose(): void {
    this.clear();
    this.scene.remove(this.root);
  }
}

/**
 * Two stand-in extracts around the yard origin. YARD has no `extracts`, so nothing in
 * the running game exercises this file yet; the host can wire `?debug=raid` to these
 * and verify the pillars, the ring and the label projection before the Spine map lands.
 * Ids stay inside the u8 the RaidState codec writes, and the radii inside its u8 tenths.
 */
export function debugExtracts(groundY: number): ExtractSnap[] {
  return [
    { id: 1, pos: { x: 16, y: groundY, z: 13 }, radius: 3.5, active: true, progress: 0 },
    { id: 2, pos: { x: -18, y: groundY, z: -11 }, radius: 3, active: false, progress: 0 },
  ];
}
